import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MissionService } from '../../../../services/mission.service';
import moment from 'moment';
import 'moment/locale/fr';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-actual-working-hours',
  templateUrl: './actual-working-hours.component.html',
  styleUrls: ['./actual-working-hours.component.scss']
})
export class ActualWorkingHoursComponent implements OnInit {
  workingHoursForm: FormGroup;
  missionDates: moment.Moment[] = [];
  isLoading = true;
  establishmentName: string = '';
  serviceName: string = '';
  missionReference: string = '';

  constructor(
    private fb: FormBuilder,
    private missionService: MissionService,
    private dialogRef: MatDialogRef<ActualWorkingHoursComponent>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: {
      missionId: number,
      startDate: string,
      endDate: string,
      establishmentName?: string,
      serviceName?: string,
      missionReference?: string,
      workingHours?: any[],
      status?: string
    }
  ) {
    this.workingHoursForm = this.fb.group({
      dailyWorkingHours: this.fb.array([])
    });

    // Initialiser les nouveaux champs
    this.establishmentName = this.data.establishmentName || '';
    this.serviceName = this.data.serviceName || '';
    this.missionReference = this.data.missionReference || '';

    // Générer les dates entre startDate et endDate
    const start = moment(this.data.startDate);
    const end = moment(this.data.endDate);
    let currentDate = start.clone();
    while (currentDate.isSameOrBefore(end)) {
      this.missionDates.push(currentDate.clone());
      this.addWorkingHoursDay(currentDate);
      currentDate.add(1, 'days');
    }

    // Charger les horaires existants
    this.loadExistingWorkingHours();
  }

  ngOnInit(): void {
    console.log("STATUS", this.data.status);
  }

  getFormattedDate(date: moment.Moment): string {
    return date.locale('fr').format('dddd D MMMM YYYY');
  }

  get dailyWorkingHours() {
    return this.workingHoursForm.get('dailyWorkingHours') as FormArray;
  }

  addWorkingHoursDay(date: moment.Moment) {
    const dayForm = this.fb.group({
      workingDate: [date.format('YYYY-MM-DD')],
      actualStartTime: ['', Validators.required],
      actualEndTime: ['', Validators.required],
      comment: ['']
    });
    this.dailyWorkingHours.push(dayForm);
  }

  removeWorkingHoursDay(index: number) {
    this.dailyWorkingHours.removeAt(index);
  }

  private loadExistingWorkingHours() {
    this.isLoading = true;
    this.missionService.getActualWorkingHours(this.data.missionId).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          // Mettre à jour les formulaires avec les données existantes
          response.forEach((hours: any) => {
            const index = this.missionDates.findIndex(date =>
              date.format('YYYY-MM-DD') === hours.workingDate
            );
            if (index !== -1) {
              const dayForm = this.dailyWorkingHours.at(index);
              dayForm.patchValue({
                actualStartTime: hours.actualStartTime,
                actualEndTime: hours.actualEndTime,
                comment: hours.comment
              });
            }
          });
        }
        this.data.status = !!response && response.length > 0 ? response[0].status : 'DRAFT';
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading working hours:', error);
        this.snackBar.open('Erreur lors du chargement des horaires', 'Fermer', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  onSubmit(isSigning: boolean = false): void {
    if (this.workingHoursForm.valid) {
      if (isSigning) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent);

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.proceedWithSubmit(isSigning);
          }
        });
      } else {
        this.proceedWithSubmit(isSigning);
      }
    }
  }

  proceedWithSubmit(isSigning: boolean): void {
    const formValue = this.workingHoursForm.value;
    const payload = {
      missionId: this.data.missionId,
      dailyWorkingHours: formValue.dailyWorkingHours.map((h: any) => ({ ...h, comment: ' ' })),
      status: isSigning ? 'WAITING_AG_VALIDATION' : 'DRAFT'
    };

    let h = payload.dailyWorkingHours.map((v: any) => ({
      ...v,
      status: payload.status
    }));

    const request = isSigning
      ? this.missionService.signAndSubmitWorkingHours(this.auth.user?.collary_id!!, this.data.missionId, h, payload.status)
      : this.missionService.updateActualWorkingHours(this.auth.user?.collary_id!!, this.data.missionId, h, payload.status);

    request.subscribe({
      next: () => {
        const message = isSigning ? 'Horaires signés et envoyés avec succès' : 'Horaires enregistrés avec succès';
        this.snackBar.open(message, 'Fermer', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error:', error);
        const message = isSigning ? 'Erreur lors de la signature des horaires' : 'Erreur lors de l\'enregistrement des horaires';
        this.snackBar.open(message, 'Fermer', { duration: 3000 });
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}